import { acceptableLuminanceDifference, hexToRGB, luminance } from "../libs/ads";
import { addFontFace, clamp, createCrossOriginImage, createInstance, initializeCanvasFont, splitStringByWrapLength } from "../libs/utils";

export async function generateImageFromCreative({ creative, template, brand, type }) {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  canvas.height = template.height;
  canvas.width = template.width;

  const resultCanvas = document.createElement("canvas");
  const resultContext = resultCanvas.getContext("2d");

  const logoSource = creative.logo.length ? creative.logo : brand?.brand_logo;

  if (!context || !canvas || !resultContext || !!creative.custom || !logoSource) return;

  resultCanvas.width = canvas.width;
  resultCanvas.height = canvas.height;

  const layers = template.overlays.filter((overlay) => overlay.layer > 1 && overlay.source.trim().length > 0);
  const base = template.overlays.filter((overlay) => overlay.layer === 1 && overlay.source.trim().length > 0);

  resultContext.fillStyle = creative.background;
  resultContext.fillRect(0, 0, resultCanvas.width, resultCanvas.height);

  for (const overlay of base) {
    try {
      const image = await createCrossOriginImage(overlay.source);
      resultContext.drawImage(image, overlay.location.x, overlay.location.y);
    } catch (error) {
      console.log("Unable to render base image: ", error);
    }
  }

  for (const mask of template.masks) {
    const resizeCanvas = document.createElement("canvas");
    const resizeContext = resizeCanvas.getContext("2d");

    const maskCanvas = document.createElement("canvas");
    const maskContext = maskCanvas.getContext("2d");

    const cropCanvas = document.createElement("canvas");
    const cropContext = cropCanvas.getContext("2d");

    if (!resizeContext || !maskContext || !cropContext) return;

    try {
      const [maskSource, photo] = await Promise.all([createCrossOriginImage(mask.source), createCrossOriginImage(creative.image)]);

      if (creative.resize) {
        const initialImageResize = creative.resize.initial_image_resize;

        resizeCanvas.height = initialImageResize.height;
        resizeCanvas.width = initialImageResize.width;

        resizeContext.drawImage(photo, 0, 0, resizeCanvas.width, resizeCanvas.height);

        const imageCrop = creative.resize.image_crop;
        cropCanvas.height = imageCrop.height;
        cropCanvas.width = imageCrop.width;

        cropContext.drawImage(
          resizeCanvas,
          imageCrop.x_start,
          imageCrop.y_start,
          cropCanvas.width,
          cropCanvas.height,
          0,
          0,
          imageCrop.x_start + cropCanvas.width,
          imageCrop.y_start + cropCanvas.height
        );

        const finalImageResize = creative.resize.final_resize;

        resizeContext.clearRect(0, 0, resizeCanvas.width, resizeCanvas.height);

        resizeCanvas.height = finalImageResize.height || cropCanvas.height;
        resizeCanvas.width = finalImageResize.width || cropCanvas.width;

        resizeContext.drawImage(cropCanvas, 0, 0, resizeCanvas.width, resizeCanvas.height);
      } else {
        const photoAspectRatio = photo.naturalWidth / photo.naturalHeight;

        resizeCanvas.height = mask.inner_dimensions.height;
        resizeCanvas.width = mask.inner_dimensions.width;

        let photoHeight, photoWidth;

        if (photo.height > photo.width) {
          photoWidth = resizeCanvas.width;
          photoHeight = photoWidth / photoAspectRatio;
        } else if (photo.width > photo.height) {
          photoHeight = resizeCanvas.height;
          photoWidth = photoHeight * photoAspectRatio;
        } else {
          photoWidth = resizeCanvas.width;
          photoHeight = photoWidth / photoAspectRatio;
        }

        if (photoWidth < resizeCanvas.width) {
          const distance = resizeCanvas.width - photoWidth;
          photoWidth = resizeCanvas.width;
          photoHeight += distance * photoAspectRatio;
        }

        if (photoHeight < resizeCanvas.height) {
          const distance = resizeCanvas.height - photoHeight;
          photoHeight = resizeCanvas.height;
          photoWidth += distance * photoAspectRatio;
        }

        resizeContext.drawImage(photo, 0, 0, photoWidth, photoHeight);
      }

      maskCanvas.height = canvas.height;
      maskCanvas.width = canvas.width;
      maskContext.drawImage(maskSource, mask.location.x, mask.location.y);
      maskContext.globalCompositeOperation = "source-in";
      maskContext.drawImage(resizeCanvas, mask.inner_location.x, mask.inner_location.y, mask.inner_dimensions.width, mask.inner_dimensions.height);

      resultContext.drawImage(maskCanvas, mask.location.x, mask.location.y);
    } catch (error) {
      console.log("Unable to render masked image: ", error);
    }
  }

  for (const overlay of layers) {
    try {
      const image = await createCrossOriginImage(overlay.source);
      resultContext.drawImage(image, overlay.location.x, overlay.location.y);
    } catch (error) {
      console.log("Unable to render overlay image: ", error);
    }
  }

  for (const object of template.objects) {
    if (object.name === "brand") {
      try {
        const logo = await createCrossOriginImage(logoSource);
        const brandResize = creative.resize?.logo_resize;
        const brandAspectRatio = logo.naturalWidth / logo.naturalHeight;
        const brandHeight = brandResize ? brandResize.height : clamp(64, logo.naturalHeight, 128);
        const brandWidth = brandResize ? brandResize.width : brandHeight * brandAspectRatio;
        resultContext.drawImage(logo, object.location.x, object.location.y, brandWidth, brandHeight);
      } catch (error) {
        console.log("Unable to render brand image: ", error);
      }
    }
  }

  for (const text of template.texts) {
    const fontFace = await addFontFace(text.font_family?.name, text.font_family?.source);

    if (fontFace.error) {
      console.log("Unable to load font: ", fontFace.error);
    }

    const font = initializeCanvasFont({ size: text.text_size, fontFamily: fontFace.name });

    const key = text.name;
    const offset = text.text_size * 1.2;

    let splitText = creative[key];

    if (text.word_index) {
      splitText = splitText.split(" ").slice(text.word_index.at(0), text.word_index.at(1)).join(" ");
    }

    const texts = splitStringByWrapLength(splitText, text.wrap_length);

    const color =
      luminance(...hexToRGB(creative.background)) < acceptableLuminanceDifference
        ? luminance(...hexToRGB(text.color)) < acceptableLuminanceDifference
          ? text.color
          : "#FFFFFF"
        : "#000000";

    if (text.name === "cta") {
      const textMetric = resultContext.measureText(splitText);
      const xOffset = 0;
      const yOffsetFactor = 1;
      const xNegativePadding = 35;
      const textHeight = textMetric.actualBoundingBoxAscent + textMetric.actualBoundingBoxDescent;
      const ctaBackgroundWidth = textMetric.width + (xOffset - xNegativePadding);
      const ctaBackgroundHeight = textHeight * yOffsetFactor * 2;
      const ctaBackgroundLocationX = text.location.x - textMetric.width / 2 - xOffset / 2 + xNegativePadding / 2;
      const ctaBackgroundLocationY = text.location.y - textHeight * yOffsetFactor;
      resultContext.fillStyle = text.is_inverted ? color : creative.background;
      resultContext.beginPath();
      resultContext.roundRect(ctaBackgroundLocationX, ctaBackgroundLocationY, ctaBackgroundWidth, ctaBackgroundHeight, 16);
      resultContext.fill();
      resultContext.closePath();
    }

    resultContext.font = font;
    resultContext.textBaseline = "middle";
    resultContext.textAlign = text.alignment;

    resultContext.fillStyle = text.name === "cta" && text.is_inverted ? creative.background : text.color;

    const longest = texts.reduce((a, b) => (a.length > b.length ? a : b));
    const metric = resultContext.measureText(longest);

    const xOffset = { left: 0, center: metric.width / 2, right: metric.width };
    const textXCoordinate = text.name === "cta" ? text.location.x : text.location.x + xOffset[text.alignment];

    texts.forEach((body, index) => {
      resultContext.fillText(body, textXCoordinate, text.location.y + index * offset);
    });
  }

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.drawImage(resultCanvas, 0, 0);

  return createInstance(Promise, (resolve, reject) => {
    if (type === "blob") {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject("Unable to create blob");
        }
      });
    } else {
      const base64 = canvas.toDataURL();
      resolve(base64);
    }
  });
}
